/* Header: announcement rotation, mega menu, burger, localization menu.

   Two behaviours carried across deliberately from the prototype:

   - clicking an open tab closes the menu; hovering the OTHER tab while one is
     already open switches panels without closing. Hover never opens a closed
     menu. That is a deliberate choice, not an oversight.
   - the scroll lock goes on both <html> and <body>. body alone stops working
     once html carries overflow-x: clip, which it does here.

   One corrected: the prototype does not render the sport tiles in the mobile
   menu at all. Here the panels are the same markup at every width, so the
   tiles come along. */
(function () {
  'use strict';

  const H = window.Hystride;
  if (!H) return;

  /* ── Announcement bar ──────────────────────────────────────────────────── */
  H.register('announcement', {
    mount(root) {
      const items = Array.from(root.querySelectorAll('[data-ann-viewport] > b'));
      if (items.length < 2) return null;

      const speed = parseInt(root.dataset.speed, 10) || 4200;
      let i = 0;
      let timer = null;

      const step = () => {
        const out = items[i];
        i = (i + 1) % items.length;
        out.classList.add('out');
        out.classList.remove('on');
        items[i].classList.add('on');
        // The outgoing message keeps .out only long enough to travel; stripping
        // it parks the element below the box ready for its next turn.
        setTimeout(() => out.classList.remove('out'), 520);
      };

      if (!H.reduceMotion()) timer = setInterval(step, speed);

      return {
        stop() {
          clearInterval(timer);
        },
      };
    },
    unmount(root, s) {
      s?.stop();
    },
  });

  /* ── Header and mega menu ──────────────────────────────────────────────── */
  H.register('header', {
    mount(root) {
      const mega = document.querySelector('[data-mega]');
      const tabs = Array.from(root.querySelectorAll('[data-mega-tab]'));
      const burger = root.querySelector('[data-burger]');
      if (!mega) return null;

      const panels = Array.from(mega.querySelectorAll('[data-mega-panel]'));
      const backdrops = Array.from(mega.querySelectorAll('.mega-bg-img'));
      const closeBtn = mega.querySelector('[data-mega-close]');

      let open = false;
      let current = -1;
      let lastFocus = null;

      const setLock = (on) => {
        // Both elements. See the header comment.
        document.documentElement.classList.toggle('lock', on);
        document.body.classList.toggle('lock', on);
      };

      const showPanel = (n, { all = false } = {}) => {
        current = n;
        panels.forEach((p, k) => (p.hidden = all ? false : k !== n));
        backdrops.forEach((b, k) => b.classList.toggle('on', k === n));
        tabs.forEach((t, k) =>
          t.setAttribute('aria-expanded', !all && k === n ? 'true' : 'false')
        );
        mega.classList.toggle('all', all);
      };

      const openMega = (n, opts) => {
        lastFocus = document.activeElement;
        open = true;
        // The CSS reveal is .mega.open — and the staggered column entrance
        // (.mega.open .mcol:nth-child(n)) hangs off the same class.
        mega.classList.add('open');
        mega.setAttribute('aria-hidden', 'false');
        setLock(true);
        showPanel(n, opts);
        if (burger) burger.setAttribute('aria-expanded', opts?.all ? 'true' : 'false');
        focusClose();
      };

      // .mega reveals via an opacity/visibility transition — focusing the
      // close button before that transition has actually finished is
      // unreliable (both a same-tick call and a deferred one via rAF/
      // setTimeout can lose the race against the triggering click/Enter's own
      // focus-on-activate default behaviour, which can re-assert focus on the
      // tab button after our call already succeeded). transitionend is the
      // signal that the panel is genuinely stable; a short timeout covers
      // reduced-motion, where the transition duration is 0 and transitionend
      // may never fire. The one-time re-check after that is a deliberate
      // belt-and-braces: if anything has stolen focus back in the meantime,
      // this is the last chance to fix it before a keyboard user starts
      // tabbing and lands outside the menu with no way in.
      const focusClose = () => {
        if (!closeBtn) return;
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          mega.removeEventListener('transitionend', onEnd);
          closeBtn.focus();
          setTimeout(() => {
            if (open && document.activeElement !== closeBtn && !mega.contains(document.activeElement)) {
              closeBtn.focus();
            }
          }, 60);
        };
        const onEnd = (e) => {
          if (e.target === mega) finish();
        };
        mega.addEventListener('transitionend', onEnd);
        setTimeout(finish, 450);
      };

      const closeMega = () => {
        open = false;
        mega.classList.remove('open', 'all');
        mega.setAttribute('aria-hidden', 'true');
        setLock(false);
        tabs.forEach((t) => t.setAttribute('aria-expanded', 'false'));
        burger?.setAttribute('aria-expanded', 'false');
        lastFocus?.focus?.();
        current = -1;
      };

      const onTab = (e) => {
        const n = parseInt(e.currentTarget.dataset.megaTab, 10);
        // Clicking the tab that is already open closes the menu.
        if (open && current === n && !mega.classList.contains('all')) {
          closeMega();
          return;
        }
        openMega(n);
      };

      // Hover switches panels but never opens a closed menu.
      const onTabHover = (e) => {
        if (!open || mega.classList.contains('all')) return;
        const n = parseInt(e.currentTarget.dataset.megaTab, 10);
        if (n !== current) showPanel(n);
      };

      const onBurger = () => {
        if (open) {
          closeMega();
          return;
        }
        // The burger shows every panel stacked, tiles included.
        openMega(0, { all: true });
      };

      const onKey = (e) => {
        if (e.key === 'Escape' && open) closeMega();
      };

      tabs.forEach((t) => {
        t.addEventListener('click', onTab);
        t.addEventListener('mouseenter', onTabHover);
      });
      burger?.addEventListener('click', onBurger);
      closeBtn?.addEventListener('click', closeMega);
      document.addEventListener('keydown', onKey);

      /* ── Localization menus ──
         Two independent pills (currency, language), each its own
         [data-localization] wrapper. Visibility is driven by [data-open] on
         that wrapper — hystride-base.css keys the menu's opacity/visibility
         and the staggered button reveal off .lang[data-open="1"], not a class
         on the menu itself. Opening one closes the other. */
      const locWraps = [
        ...root.querySelectorAll('[data-localization]'),
        // The menu's copy of the pills (mobile) lives in [data-mega], which is
        // a sibling of this section's root, not inside it.
        ...mega.querySelectorAll('[data-localization]'),
      ];
      const locToggles = locWraps.map((w) => w.querySelector('[data-lang-toggle]'));

      const closeLoc = (wrap) => {
        wrap.dataset.open = '0';
        wrap.querySelector('[data-lang-toggle]')?.setAttribute('aria-expanded', 'false');
      };

      const onLocToggle = (wrap) => () => {
        const isOpen = wrap.dataset.open === '1';
        locWraps.forEach(closeLoc);
        if (!isOpen) {
          wrap.dataset.open = '1';
          wrap.querySelector('[data-lang-toggle]')?.setAttribute('aria-expanded', 'true');
        }
      };
      const onLocHandlers = locWraps.map(onLocToggle);

      const onDocClick = (e) => {
        if (e.target.closest('[data-localization]')) return;
        locWraps.forEach(closeLoc);
      };

      locToggles.forEach((toggle, i) => toggle?.addEventListener('click', onLocHandlers[i]));
      document.addEventListener('click', onDocClick);

      return { tabs, onTab, onTabHover, burger, onBurger, closeBtn, closeMega, onKey, locToggles, onLocHandlers, onDocClick };
    },

    unmount(root, s) {
      if (!s) return;
      s.tabs.forEach((t) => {
        t.removeEventListener('click', s.onTab);
        t.removeEventListener('mouseenter', s.onTabHover);
      });
      s.burger?.removeEventListener('click', s.onBurger);
      s.closeBtn?.removeEventListener('click', s.closeMega);
      document.removeEventListener('keydown', s.onKey);
      s.locToggles.forEach((toggle, i) => toggle?.removeEventListener('click', s.onLocHandlers[i]));
      document.removeEventListener('click', s.onDocClick);
      document.documentElement.classList.remove('lock');
      document.body.classList.remove('lock');
    },
  });
})();
